import {Injectable} from '@angular/core';
import {
  arrayRemove,
  arrayUnion,
  collection,
  collectionData,
  deleteField,
  doc,
  docData,
  DocumentData,
  Firestore,
  Query,
  query,
  setDoc,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import {combineLatest, Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {FirebaseService} from '../../../shared-utilities/services-old/firebase.service';
import {hasAccessToRule} from '../../shared-utils/user-access/user-access.utils';
import {path_user_access_userId} from '../database-paths';
import {
  IColleagues,
  IColleagueUser,
  IGroupAccessPerStore,
  ILastSeen,
  ILastSeenByStore,
  IUser,
  IUserAccess,
} from '../../../features/user-access/models';
import {UserAccessStatus, UserAccessStatusEnum} from '../../../features/user-access/models/user-access.enum';
import {firebaseTimeStampToDate} from '../../shared-utils/firestore/firestore.utils';
import {createSharedPaths} from '../database-paths.shared';

@Injectable({
  providedIn: 'root',
})
export class CollectionUserAccessColleaguesService {

  constructor(
    private firebaseService: FirebaseService,
    private firestore: Firestore,
  ) {
  }

  public getColleagues(userId: string): Observable<IColleagues> {
    const docRef = doc(this.firestore, `/user_access/${userId}`);
    return docData(docRef, {idField: 'id'}).pipe(
      switchMap((userAccess: IUserAccess) => {
        if (userAccess) {
          const storeList = userAccess.storeList;
          return this.getUsersForStores$(storeList, userId);
        } else {
          return of({} as IColleagues);
        }
      }),
      catchError((error) => {
        console.error('Error fetching user access:', error);
        return of({});
      }),
    );
  }

  public async setColleagues(
    newEmail: string,
    groupAccessPerStore: IGroupAccessPerStore,
    colleague: IColleagueUser | undefined,
    merge: boolean = false,
    newUser: boolean = false,
    resendInvite: boolean = false,
  ): Promise<void> {
    const storesToRemove: string[] = [];
    const storesWithAccessCode: Record<string, number> = {};

    for (const id of Object.keys(groupAccessPerStore)) {
      const groupAccessCode = groupAccessPerStore[id].groupAccessCode;

      if (this.shouldRemoveStoreAccess(groupAccessCode)) {
        storesToRemove.push(id);
      } else {
        storesWithAccessCode[id] = groupAccessCode;
      }
    }

    for (const id of storesToRemove) {
      try {
        const docRef = doc(this.firestore, path_user_access_userId(newEmail));
        await updateDoc(docRef, {
          adminAccessStores: arrayRemove(id),
          [`stores.${id}`]: deleteField(),
          storeList: arrayRemove(id),
        });
      } catch (error) {
        console.error('Error deleting store entry:', error);
      }
    }

    for (const storeId of Object.keys(storesWithAccessCode)) {
      try {
        const docRef = doc(this.firestore, path_user_access_userId(newEmail));
        const hasStaffAccess: boolean = hasAccessToRule(storesWithAccessCode[storeId], 0);
        await updateDoc(docRef, {
          adminAccessStores: hasStaffAccess ? arrayUnion(storeId) : arrayRemove(storeId),
          [`stores.${storeId}`]: storesWithAccessCode[storeId],
          storeList: arrayUnion(storeId),
        });
      } catch (error) {
        console.error('Error adding store entry:', error);
      }
    }

    const {storeList, stores} = this.resolveStoresAfterEdit(
      merge,
      colleague,
      storesWithAccessCode,
      storesToRemove,
    );

    const userAccessData: IUserAccess = {
      features: 7,
      storeList,
      stores,
      adminAccessStores: [],
      created: colleague?.created ?? new Date(),
      lastPasswordLinkTimestamp: resendInvite || newUser ? new Date() : colleague?.lastPasswordLinkTimestamp,
      status: this.resolveUserAccessStatus(colleague, newUser, resendInvite),
    } as IUserAccess;

    await this.setUserAccessDocumentForOtherUser(newEmail, userAccessData, merge);
    if (newUser) {
      const userDocument: IUser = {
        id: newEmail,
        pp: '',
        userName: newEmail,
      };
      await this.firebaseService.setUserDocument(newEmail, userDocument, merge);
    }

  }

  /**
   * Grants store access to a user who already has a user_access document but is not
   * currently visible as a colleague. Does not write status so VERIFIED/EXPIRED is preserved.
   */
  public async setNonAccessibleColleague(newEmail: string, groupAccessPerStore: IGroupAccessPerStore): Promise<void> {
    const storeList: string[] = [];
    const storesWithAccessCode: Record<string, number> = {};

    for (const storeId of Object.keys(groupAccessPerStore)) {
      const groupAccessCode = groupAccessPerStore[storeId].groupAccessCode;

      if (!this.shouldRemoveStoreAccess(groupAccessCode)) {
        storeList.push(storeId);
        storesWithAccessCode[storeId] = groupAccessCode;
      }
    }

    const userAccessData: Partial<IUserAccess> = {
      features: 7,
      storeList: storeList,
      stores: storesWithAccessCode,
      adminAccessStores: [],
    };

    await this.setUserAccessDocumentUnion(newEmail, userAccessData);

  }

  /**
   * Returns true when the access code means the store should be removed from the user.
   * Null/undefined come from strip-access; 0 is the legacy "no permissions" removal value.
   */
  private shouldRemoveStoreAccess(groupAccessCode: number | null | undefined): boolean {
    return groupAccessCode === null || groupAccessCode === undefined || groupAccessCode === 0;
  }

  /**
   * Resolves the user_access status for a save.
   * New invites / resends start as PENDING; existing users keep their current status
   * so role edits do not flip VERIFIED users back to PENDING.
   */
  private resolveUserAccessStatus(
    colleague: IColleagueUser | undefined,
    newUser: boolean,
    resendInvite: boolean,
  ): UserAccessStatus {
    if (newUser || resendInvite) {
      return UserAccessStatusEnum.PENDING;
    }
    return colleague?.status ?? UserAccessStatusEnum.PENDING;
  }

  /**
   * Builds the stores map and storeList after applying adds/removes.
   * When merging, preserves existing stores that were not part of this edit batch.
   */
  private resolveStoresAfterEdit(
    merge: boolean,
    colleague: IColleagueUser | undefined,
    storesWithAccessCode: Record<string, number>,
    storesToRemove: string[],
  ): {storeList: string[]; stores: Record<string, number>} {
    if (!merge || !colleague) {
      return {
        storeList: Object.keys(storesWithAccessCode),
        stores: storesWithAccessCode,
      };
    }

    const stores: Record<string, number> = {...(colleague.stores ?? {})};
    for (const storeId of storesToRemove) {
      delete stores[storeId];
    }
    Object.assign(stores, storesWithAccessCode);

    for (const storeId of Object.keys(stores)) {
      if (this.shouldRemoveStoreAccess(stores[storeId])) {
        delete stores[storeId];
      }
    }

    return {
      storeList: Object.keys(stores),
      stores,
    };
  }

  // TODO: Should we add additional permissions here to inform the user if they do not have access, even though firebase rules will make sure
  private setUserAccessDocumentForOtherUser(userId: string, updates: IUserAccess, merge: boolean = false): Promise<void> {
    // Get the access code and determine whether it is an admin to update the adminAccessStores array accordingly,
    // if the user is not admin or no longer admin it will be cleared from the adminAccessStores array.
    updates.adminAccessStores = [];

    for (const storeId of Object.keys(updates.stores)) {
      if (hasAccessToRule(updates.stores[storeId], 0)) {
        updates.adminAccessStores.push(storeId);
      }
    }
    return new Promise<void>((resolve, reject) => {
      const docRef = doc(this.firestore, `user_access/${userId}`);
      setDoc(docRef, updates, {merge: true})
        .then(() => {
          resolve();
        })
        .catch(error => {
          error.message = `Error setting document in user_access collection for document: ${userId}\n${error.message}`;
          reject(error);
        });
    });
  }

  private async setUserAccessDocumentUnion(userId: string, updates: Partial<IUserAccess>): Promise<void> {
    const updateData: Record<string, unknown> = {};
    const adminStoresToAdd: string[] = [];
    const adminStoresToRemove: string[] = [];

    if (updates.stores) {
      for (const storeId of Object.keys(updates.stores)) {
        if (hasAccessToRule(updates.stores[storeId], 0)) {
          adminStoresToAdd.push(storeId);
        } else {
          adminStoresToRemove.push(storeId);
        }
      }
    }

    for (const key of Object.keys(updates)) {
      if (key === 'adminAccessStores') {
        continue;
      }

      const value = updates[key as keyof IUserAccess];

      if (key === 'stores' && value && typeof value === 'object' && !Array.isArray(value)) {
        for (const storeId of Object.keys(value as Record<string, number>)) {
          updateData[`stores.${storeId}`] = (value as Record<string, number>)[storeId];
        }
      } else if (Array.isArray(value)) {
        updateData[key] = arrayUnion(...value);
      } else {
        updateData[key] = value;
      }
    }

    if (adminStoresToAdd.length > 0) {
      updateData['adminAccessStores'] = arrayUnion(...adminStoresToAdd);
    }

    try {
      const docRef = doc(this.firestore, path_user_access_userId(userId));
      if (Object.keys(updateData).length > 0) {
        await updateDoc(docRef, updateData);
      }
      for (const storeId of adminStoresToRemove) {
        await updateDoc(docRef, {
          adminAccessStores: arrayRemove(storeId),
        });
      }
    } catch (error) {
      console.error('Error updating user_access document:', error);
    }
  }


  private getUsersForStores$(storeIds: string[], currentUserId: string): Observable<IColleagues> {
    const storeIdsCopy = [...storeIds];
    const queries: Observable<unknown>[] = [];
    while (storeIdsCopy.length) {
      const chunk = storeIdsCopy.splice(0, 10);
      const collectionRef = collection(this.firestore, '/user_access/');
      const q = query(collectionRef, where('storeList', 'array-contains-any', chunk));
      const queryObservable = collectionData(q, {idField: 'userId'});
      queries.push(queryObservable);
    }

    return combineLatest(queries).pipe(
      switchMap((userAccessResults: IUserAccess[]) => {
        const allUserAccesses: IUserAccess[] = [].concat(...userAccessResults);
        const filteredUserAccesses = allUserAccesses.filter((user: IUserAccess) => !user.isServer);
        const userMap: Record<string, IUserAccess> = {};
        const currentUserAccess = filteredUserAccesses.find((userAccess: IUserAccess) =>
          userAccess.userId === currentUserId);
        if (!currentUserAccess) return of({});
        filteredUserAccesses.forEach((userAccess: IUserAccess) => {
          const userId = userAccess.userId;
          const accessData = {...userAccess} as IUserAccess;
          const hasAdminAccess = accessData.storeList.some((storeId: string) =>
            currentUserAccess.adminAccessStores?.includes(storeId));
          if (accessData.storeList.length > 0 && currentUserId !== userId && hasAdminAccess) {
            userMap[userId] = accessData;
          }
        });

        const userIds = Object.keys(userMap);

        // Combine user data with last seen timestamps
        return combineLatest([
          this.fetchUsersData(userIds),
          this.fetchAllLastSeenTimestamps(storeIds),
        ]).pipe(
          map(([usersData, lastSeenByStore]) => {
            const colleagues: IColleagues = {};
            userIds.forEach(userId => {
              const userAccess = userMap[userId];
              const userData = usersData[userId];

              // Find the most recent last seen timestamp for this user across all stores
              let latestTimestamp: Date = null;
              Object.keys(lastSeenByStore).forEach(storeId => {
                const storeLastSeen = lastSeenByStore[storeId];
                if (storeLastSeen[userId]) {
                  const timestamp = storeLastSeen[userId];
                  if (!latestTimestamp || timestamp > latestTimestamp) {
                    latestTimestamp = timestamp;
                  }
                }
              });

              colleagues[userId] = {
                ...userAccess,
                userData: userData || null,
                lastSeen: latestTimestamp,
                lastSeenByStore: Object.keys(lastSeenByStore)
                  .filter(storeId => lastSeenByStore[storeId][userId])
                  .reduce((acc, storeId) => {
                    acc[storeId] = lastSeenByStore[storeId][userId];
                    return acc;
                  }, {}),
              };
            });
            return colleagues;
          }),
        );
      }),
      catchError((error) => {
        console.error('Error fetching users for stores:', error);
        return of({});
      }),
    );
  }

  private fetchUsersData(userIds: string[]): Observable<Record<string, IUser>> {
    if (userIds.length === 0) {
      return of({});
    }
    const validUserIds = userIds.filter((id: string) => id);
    const queries: Observable<(DocumentData | (DocumentData & {
      id: string;
    }))>[] = [];
    while (validUserIds.length > 0) {
      const chunk = validUserIds.splice(0, 9);
      const collectionRef = collection(this.firestore, 'users');
      const q: Query = query(collectionRef, where('__name__', 'in', chunk));
      const queryObservable = collectionData(q, {idField: 'id'});
      queries.push(queryObservable);
    }

    return combineLatest(queries).pipe(
      map((results: IUser[][]) => {
        const allUsers = [].concat(...results);
        const usersData: Record<string, IUser> = {};
        allUsers.forEach((user: IUser) => {
          usersData[user.id] = user;
        });
        return usersData;
      }),
      catchError((error) => {
        console.error('Error fetching user data:', error);
        return of({});
      }),
    );
  }

  /**
   * Fetches last seen timestamps for all users in a store
   * @param storeId The ID of the store to fetch timestamps for
   * @returns Observable of last seen data mapped by email
   */
  private fetchLastSeenTimestamps(storeId: string): Observable<ILastSeen> {
    const path = createSharedPaths().setStoreId(storeId).messagesFromApp();
    const docRef = doc(this.firestore, path);
    return docData(docRef, {idField: 'id'})
      .pipe(
        map((data: ILastSeen) => data || {}),
        catchError((error) => {
          console.error(`Error fetching last seen data for store ${storeId}:`, error);
          return of({});
        }),
      );
  }

  /**
   * Fetches last seen timestamps for multiple stores and organizes them by storeId and email
   * @param storeIds Array of store IDs to fetch timestamps for
   * @returns Observable of last seen timestamps organized by store and email
   */
  private fetchAllLastSeenTimestamps(storeIds: string[]): Observable<ILastSeenByStore> {
    if (!storeIds || storeIds.length === 0) {
      return of({});
    }

    const storeQueries = storeIds.map(storeId => {
      return this.fetchLastSeenTimestamps(storeId).pipe(
        map(lastSeenData => ({storeId, lastSeenData})),
      );
    });

    return combineLatest(storeQueries).pipe(
      map(results => {
        const lastSeenByStore: ILastSeenByStore = {};

        results.forEach(({storeId, lastSeenData}) => {
          if (!lastSeenData) return;

          // Initialize the store entry if it doesn't exist
          if (!lastSeenByStore[storeId]) {
            lastSeenByStore[storeId] = {};
          }

          // Process each user's timestamp in this store
          Object.keys(lastSeenData).forEach(email => {
            const userData = lastSeenData[email];
            if (!userData || !userData.time) return;

            // Convert Firebase timestamp to Date object
            const timestamp = firebaseTimeStampToDate(userData.time);

            // Store the timestamp by email within this store
            lastSeenByStore[storeId][email] = timestamp;
          });
        });

        return lastSeenByStore;
      }),
      catchError((error) => {
        console.error('Error fetching last seen timestamps:', error);
        return of({});
      }),
    );
  }
}
